import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { Boss } from '../entities/Boss';
import { Projectile } from '../entities/Projectile';
import { PowerUp } from '../entities/PowerUp';
import { Projectile as WeaponProjectile } from './WeaponSystem';
import { checkCollision } from '../entities/Entity';

export class CollisionSystem {
  public checkProjectileEnemyCollisions(
    enemies: Enemy[],
    projectiles: WeaponProjectile[]
  ): Array<{ enemy: Enemy; projectile: WeaponProjectile }> {
    const hits: Array<{ enemy: Enemy; projectile: WeaponProjectile }> = [];

    for (const enemy of enemies) {
      if (!enemy.active) continue;

      for (const projectile of projectiles) {
        if (!projectile.active || !projectile.friendly) continue;

        const projBounds = {
          x: projectile.x - projectile.width / 2,
          y: projectile.y - projectile.height / 2,
          width: projectile.width,
          height: projectile.height
        };
        
        const enemyBounds = enemy.getBounds();
        
        // AABB collision check
        if (projBounds.x < enemyBounds.x + enemyBounds.width &&
            projBounds.x + projBounds.width > enemyBounds.x &&
            projBounds.y < enemyBounds.y + enemyBounds.height &&
            projBounds.y + projBounds.height > enemyBounds.y) {
          hits.push({ enemy, projectile });
        }
      }
    }

    return hits;
  }
  
  public checkPlayerBulletEnemies(
    projectiles: Projectile[],
    enemies: Enemy[]
  ): { enemy: Enemy; projectile: Projectile }[] {
    const collisions: { enemy: Enemy; projectile: Projectile }[] = [];

    for (const projectile of projectiles) {
      if (!projectile.active || !projectile.isFriendly()) continue;

      for (const enemy of enemies) {
        if (!enemy.active) continue;

        if (checkCollision(projectile, enemy)) {
          collisions.push({ enemy, projectile });
          
          // Deactivate projectile if not piercing
          if (!projectile.isPiercing()) {
            projectile.deactivate();
            break; // Move to next projectile
          }
        }
      }
    }

    return collisions;
  }

  public checkPlayerBulletBoss(
    projectiles: Projectile[],
    boss: Boss | null
  ): { boss: Boss; projectile: Projectile }[] {
    const collisions: { boss: Boss; projectile: Projectile }[] = [];

    if (!boss || !boss.active) return collisions;

    for (const projectile of projectiles) {
      if (!projectile.active || !projectile.isFriendly()) continue;

      if (checkCollision(projectile, boss)) {
        collisions.push({ boss, projectile });
        
        if (!projectile.isPiercing()) {
          projectile.deactivate();
        }
      }
    }

    return collisions;
  }

  public checkPlayerEnemies(player: Player, enemies: Enemy[]): Enemy[] {
    const collisions: Enemy[] = [];

    if (!player.active || player.isInvulnerable()) return collisions;

    for (const enemy of enemies) {
      if (!enemy.active) continue;

      if (checkCollision(player, enemy)) {
        collisions.push(enemy);
      }
    }

    return collisions;
  }

  public checkPlayerBoss(player: Player, boss: Boss | null): boolean {
    if (!boss || !boss.active || !player.active || player.isInvulnerable()) {
      return false;
    }

    return checkCollision(player, boss);
  }

  public checkPlayerPowerUps(player: Player, powerups: PowerUp[]): PowerUp[] {
    const collisions: PowerUp[] = [];

    if (!player.active) return collisions;

    const collectRadius = player.hasAutoCollect() ? 80 : 0;

    for (const powerup of powerups) {
      if (!powerup.active) continue;

      // Check direct collision
      if (checkCollision(player, powerup)) {
        collisions.push(powerup);
        continue;
      }

      // Check auto-collect radius
      if (collectRadius > 0) {
        const dx = player.x - powerup.x;
        const dy = player.y - powerup.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < collectRadius) {
          collisions.push(powerup);
        }
      }
    }

    return collisions;
  }

  public checkEnemyBulletPlayer(
    projectiles: Projectile[],
    player: Player
  ): Projectile[] {
    const collisions: Projectile[] = [];

    if (!player.active || player.isInvulnerable()) return collisions;

    for (const projectile of projectiles) {
      if (!projectile.active || projectile.isFriendly()) continue;

      if (checkCollision(projectile, player)) {
        collisions.push(projectile);
        projectile.deactivate();
      }
    }

    return collisions;
  }

  // Check if enemies touch the chain people - returns enemy and segment index
  public checkEnemyChainCollisions(
    enemies: Enemy[],
    chainBounds: Array<{ x: number; y: number; width: number; height: number; index: number }>
  ): Array<{ enemy: Enemy; segmentIndex: number }> {
    const collisions: Array<{ enemy: Enemy; segmentIndex: number }> = [];

    for (const enemy of enemies) {
      if (!enemy.active) continue;

      const enemyBounds = enemy.getBounds();

      for (const personBounds of chainBounds) {
        // AABB collision check
        if (enemyBounds.x < personBounds.x + personBounds.width &&
            enemyBounds.x + enemyBounds.width > personBounds.x &&
            enemyBounds.y < personBounds.y + personBounds.height &&
            enemyBounds.y + enemyBounds.height > personBounds.y) {
          collisions.push({ enemy, segmentIndex: personBounds.index });
          break; // Enemy can only hit one person at a time
        }
      }
    }

    return collisions;
  }

  // Check enemy-enemy collisions for shield bounce effects
  public checkEnemyEnemyCollisions(enemies: Enemy[]): Array<{ enemy1: Enemy; enemy2: Enemy }> {
    const collisions: Array<{ enemy1: Enemy; enemy2: Enemy }> = [];

    for (let i = 0; i < enemies.length; i++) {
      const enemy1 = enemies[i];
      if (!enemy1.active) continue;

      for (let j = i + 1; j < enemies.length; j++) {
        const enemy2 = enemies[j];
        if (!enemy2.active) continue;

        if (checkCollision(enemy1, enemy2)) {
          collisions.push({ enemy1, enemy2 });
        }
      }
    }

    return collisions;
  }
}
